import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  Tenant, 
  Product, 
  User
} from '../../types';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { 
  Building, 
  Users, 
  Settings, 
  Package, 
  Calendar, 
  Shield, 
  Server, 
  Tag, 
  Plus,
  Info,
  Mail,
  Clock,
  Lock,
  FileText,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  CheckCheck,
  Edit,
  Download
} from 'lucide-react';
import { formatDate, getTenantLevelLabel, getTenantTypeLabel } from '../../utils/format';
import { getProducts, getTenant, updateProductIntegrationStatus, deleteProductIntegration, getUserNameById, getMultipleIntegrationsStatus, updateLoggerCustomerStatus, getUsers, generateConfigFile } from '../../services/api';
import Tabs from '../ui/Tabs';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import TenantFormModal from './TenantFormModal';
import IntegrateProductModal from '../products/IntegrateProductModal';
import ConfirmationModal from '../common/ConfirmationModal';
import DocumentViewer from './DocumentViewer';

// Define SOWDocument interface locally
interface SOWDocument {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  created_at: string;
}

interface TenantDetailModalProps {
  tenant: Tenant | null;
  isOpen: boolean;
  onClose: () => void;
}

// Integration Details Modal component
interface IntegrationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: any;
  productName: string;
}

const IntegrationDetailsModal: React.FC<IntegrationDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  integration, 
  productName 
}) => {
  if (!integration) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${productName} Configuration Details`}
      size="lg"
    >
      <div className="space-y-6">
        <div className="bg-[#1j8a3e] p-4 rounded border border-[#2d4064]">
          <h4 className="text-sm font-medium mb-4 text-primary flex items-center">
            <FileText size={14} className="mr-1.5" />
            Integration Configuration
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {integration.values && Object.entries(integration.values).map(([key, value]) => (
              <div key={key} className="flex flex-col">
                <span className="text-xs text-gray-400">{key}</span>
                <span className="text-sm text-white bg-[#1j8a3e] px-2 py-1.5 rounded border border-[#273353]">
                  {typeof value === 'object' ? 
                    JSON.stringify(value) : 
                    key.includes('secret') || key.includes('key') || key.includes('password') ? 
                    '••••••••' : 
                    String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Integration status */}
        <div className="bg-[#1j8a3e] p-4 rounded border border-[#2d4064]">
          <h4 className="text-sm font-medium mb-4 text-primary flex items-center">
            <Info size={12} className="mr-1.0" />
            Integration Status
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">Status</span>
              <span className="text-sm text-white">
                <span className={`inline-flex items-center px-2 py-1 rounded ${integration.active ? 'bg-green-500/20 text-green-400' : 'bg-slate-600/50 text-gray-400'}`}>
                  <span className={`w-2 h-2 rounded-full mr-2 ${integration.active ? 'bg-green-400' : 'bg-gray-500'}`}></span>
                  {integration.active ? 'Active' : 'Inactive'}
                </span>
              </span>
            </div>
            <div className="flex flex-col">
              
            </div>
          </div>
        </div>
        
        {/* Integration metadata */}
        <div className="bg-[#1j8a3e] p-4 rounded border border-[#2d4064]">
          <h4 className="text-sm font-medium mb-4 text-primary flex items-center">
            <Clock size={14} className="mr-1.5" />
            Integration Timeline
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">Created</span>
              <span className="text-sm text-white bg-[#1j8a3e] px-2 py-1.5 rounded border border-[#273353]">
                {integration.created_at ? formatDate(integration.created_at) : '—'}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">Last Updated</span>
              <span className="text-sm text-white bg-[#1j8a3e] px-2 py-1.5 rounded border border-[#273353]">
                {integration.updated_at ? formatDate(integration.updated_at) : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end pt-4 border-t border-slate-700/30 mt-6">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

const TenantDetailModal: React.FC<TenantDetailModalProps> = ({ tenant: initialTenant, isOpen, onClose }) => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'details' | 'documents' | 'products' | 'users'>('details');
  const [products, setProducts] = useState<Product[]>([]);
  const [productIntegrations, setProductIntegrations] = useState<Record<string, any>>({});
  // Guard against race conditions when quickly switching tenants/tabs
  const productsFetchIdRef = useRef<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>(initialTenant);
  const [isLoadingTenant, setIsLoadingTenant] = useState(false);
  const [creatorName, setCreatorName] = useState<string | null>(null);
  const [updaterName, setUpdaterName] = useState<string | null>(null);
  const [parentTenantName, setParentTenantName] = useState<string | null>(null);

  // Add product filter state
  const [productFilter, setProductFilter] = useState<'all' | 'integrated' | 'not-integrated'>('integrated');
  // Add product type filter
  const [productTypeFilter, setProductTypeFilter] = useState<'all' | 'saas' | 'logger'>('all');

  // Asset Repo IOCs (from onboarding Asset Repo step)
  const assetManagementIocs = (tenant as any)?.['asset_management_iocs'] as any[] | undefined;

  // Add state for edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { canViewTenants, canEditTenants, canManageIntegrations } = usePermissions();
  
  // Add state for product integration modal
  const [selectedProductForIntegration, setSelectedProductForIntegration] = useState<{product: Product, isIntegrated: boolean} | null>(null);
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  
  // Add state for integration details modal
  const [integrationDetailsModal, setIntegrationDetailsModal] = useState<{
    isOpen: boolean;
    integration: any;
    productName: string;
  }>({
    isOpen: false,
    integration: null,
    productName: ''
  });
  
  // Add state for delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState<{productId: string, productName: string} | null>(null);

  // Add state for password visibility
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});
  const [copiedFields, setCopiedFields] = useState<Record<string, boolean>>({});

  // Add state for tenant users
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  
  // Add state for document viewer
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<SOWDocument | null>(null);

  const fetchFullTenantDetails = useCallback(async (tenantId: string) => {
    if (!tenantId) return;

    setIsLoadingTenant(true);
    try {
      const fullTenantData = await getTenant(tenantId);
      
      if (fullTenantData && initialTenant) {
        const mergedTenant = {
          ...initialTenant,
          ...fullTenantData,
        };
        setTenant(mergedTenant);
      } else {
        setTenant(fullTenantData);
      }
    } catch (error) {
      console.error('Error fetching full tenant details:', error);
      
      if (error instanceof Error) {
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      
      setTenant(initialTenant);
    } finally {
      setIsLoadingTenant(false);
    }
  }, [initialTenant]);

  const fetchProducts = useCallback(async () => {
    if (!tenant || !tenant.id) {
      console.error('Cannot fetch products: No tenant ID available');
      return;
    }
    
    setIsLoading(true);
    const requestId = ++productsFetchIdRef.current;
    
    try {
      const params = productTypeFilter !== 'all' ? { type: productTypeFilter } : undefined;
      const productsData = await getProducts(params);
      
      if (!productsData) {
        console.error('No products data returned');
        setProducts([]);
        setIsLoading(false);
        return;
      }
      
      productsData.sort((a, b) => a.name.localeCompare(b.name));
      if (productsFetchIdRef.current !== requestId) {
        return;
      }
      setProducts(productsData);
      
      const productIds = productsData.map(p => p.id || p._id as string).filter(Boolean);
      
      if (productIds.length > 0) {
        try {
          const integrationsData = await getMultipleIntegrationsStatus([tenant.id], productIds);
          const integrations: Record<string, any> = {};
          
          if (integrationsData && typeof integrationsData === 'object') {
            productIds.forEach(prodId => {
              if (integrationsData[prodId] && integrationsData[prodId][tenant.id]) {
                const integration = integrationsData[prodId][tenant.id];
                const isActive = 
                  integrationsData.active_status?.[prodId]?.[tenant.id] !== undefined
                    ? integrationsData.active_status[prodId][tenant.id]
                    : integration.active;
                
                integrations[prodId] = {
                  ...integration,
                  active: isActive
                };
              } else {
                integrations[prodId] = null;
              }
            });
          }
          
          if (productsFetchIdRef.current !== requestId) {
            return;
          }
          setProductIntegrations(integrations);
        } catch (err) {
          console.error('Error fetching integrations:', err);
        }
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      if (productsFetchIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [tenant, productTypeFilter]);

  // Fetch the full tenant details when the modal opens
  useEffect(() => {
    if (initialTenant && isOpen) {
      fetchFullTenantDetails(initialTenant.id);
    }
  }, [initialTenant, isOpen, fetchFullTenantDetails]);

  // Reset product state and fetch when tenant changes or modal opens on products tab
  useEffect(() => {
    if (tenant && isOpen) {
      // Only reset when products tab is visible to avoid unnecessary flicker elsewhere
      if (activeTab === 'products') {
        setProducts([]);
        setProductIntegrations({});
      }
      fetchProducts();
    }
  }, [tenant, isOpen, activeTab, fetchProducts]);

  // Fetch user names when tenant changes
  useEffect(() => {
    if (tenant) {
      // Fetch creator name
      if (tenant.created_by) {
        getUserNameById(tenant.created_by)
          .then(name => {
            setCreatorName(name);
          })
          .catch(err => console.error('Error fetching creator name:', err));
      }
      
      // Fetch updater name
      const updaterId = (tenant as any).updated_by;
      if (updaterId) {
        getUserNameById(updaterId)
          .then(name => {
            setUpdaterName(name);
          })
          .catch(err => console.error('Error fetching updater name:', err));
      }
      
      // Fetch parent tenant name
      if (tenant.parent_tenant_id) {
        getTenant(tenant.parent_tenant_id)
          .then(parentTenant => {
            setParentTenantName(parentTenant.name);
          })
          .catch(err => console.error('Error fetching parent tenant name:', err));
      }
    }
  }, [tenant]);

  // Handle toggling product integration active status
  const toggleIntegrationStatus = async (productId: string, currentStatus: boolean) => {
    if (!tenant || !tenant.id) return;
    
    try {
      // Get the product to determine its type
      const product = products.find(p => (p.id || p._id) === productId);
      if (!product) return;
      
      const productType = (product as any).type || 'saas';
      
      // Toggling integration status
      
      if (productType === 'logger') {
        // Use logger-specific API
        await updateLoggerCustomerStatus({
          customer_id: tenant.id,
          product_id: productId,
          is_active: !currentStatus
        });
      } else {
        // Use standard SaaS API
        await updateProductIntegrationStatus({
          customer_id: tenant.id,
          product_id: productId,
          is_active: !currentStatus
        });
      }
      
      // Update local state
      setProductIntegrations(prev => ({
        ...prev,
        [productId]: {
          ...(prev[productId] || {}),
          active: !currentStatus
        }
      }));
      
      // Show success toast
      toast.success(`${product.name} integration ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Failed to toggle integration status:', error);
      toast.error('Failed to update integration status');
    }
  };

  // Fetch tenant users
  const fetchTenantUsers = useCallback(async () => {
    if (!tenant || !tenant.id) return;
    
    setIsLoadingUsers(true);
    try {
      const users = await getUsers({ tenant_id: tenant.id });
      setTenantUsers(users);
    } catch (error) {
      console.error('Error fetching tenant users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [tenant]);

  // Load tenant users when tab changes to users
  useEffect(() => {
    if (activeTab === 'users' && tenant?.id) {
      fetchTenantUsers();
    }
  }, [activeTab, tenant?.id, fetchTenantUsers]);

  if (!tenant) return null;

  // Helper function to display boolean values
  const displayBooleanValue = (value: boolean | undefined): string => {
    if (value === undefined || value === null) return 'Disabled';
    return value ? 'Enabled' : 'Disabled';
  };

  // Show loading state when fetching tenant details
  if (isLoadingTenant) {
    return (
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        title="Loading Tenant Details"
        size="lg"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin h-12 w-12 border-b-2 border-primary rounded-full mb-4"></div>
          <p className="text-text-muted-dark">Loading complete tenant information...</p>
        </div>
      </Modal>
    );
  }

  // Add a function to check if user has access to view certain sections
  const canViewSection = (section: string): boolean => {
    if (!currentUser) {
      return false;
    }

    // RBAC: explicit view tenants permission
    try {
      if (canViewTenants && typeof canViewTenants === 'function' && canViewTenants()) {
        return true;
      }
    } catch {}

    // L0 users (Platform Owners) have full access to everything regardless of role
    if (currentUser.tenant_level === 'L0') {
      return true;
    }

    // Admin users at any level can see everything
    if (currentUser.role?.toLowerCase() === 'admin') {
      return true;
    }

    // L2 users (Partners) can see their own tenant and child organizations (L3)
    if (currentUser.tenant_level === 'L2') {
      if (tenant?.parent_tenant_id === currentUser.tenant_id) return true;
      if (tenant?.id === currentUser.tenant_id) return true;
      return false;
    }

    // L3 users can only see their own tenant
    if (currentUser.tenant_level === 'L3') {
      return tenant?.id === currentUser.tenant_id;
    }

    // By default, restrict access
    return false;
  };
  
  // Helper function for type safe property access with debugging
  const getTenantProperty = (property: string, defaultValue: any = '—'): any => {
    const value = (tenant as any)[property];
    // Quietly fall back to default when missing (no console noise)
    // For object values, return a well-formatted version or the default
    if (typeof value === 'object' && value !== null) {
      return value;
    }
    
    return value || defaultValue;
  };

  // Modified formatUserDisplay function to use the fetched names
  const formatUserDisplay = (userId: string | undefined, fetchedName: string | null = null): string => {
    if (!userId) return '—';
    
    // If we have a fetched name from the API, use that
    if (fetchedName) {
      return fetchedName;
    }
    
    // If it's already in the format "User XXXXX", keep it as is
    if (userId.startsWith('User ')) {
      return userId;
    }
    
    // Check if it's an email format already
    if (userId.includes('@')) {
      return userId;
    }
    
    // Check if it's a UUID format (long string with hyphens)
    if (userId.includes('-') && userId.length > 20) {
      return 'Admin User';
    }
    
    // If it's a MongoDB ObjectID, show a loading state
    if (/^[0-9a-f]{24}$/i.test(userId)) {
      return 'Loading user...';
    }
    
    // If it appears to be a database ID (typically numeric or alphanumeric)
    return 'User ' + userId;
  };

  // Toggle password visibility
  const toggleSecretVisibility = (key: string) => {
    setVisibleSecrets(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Copy field value to clipboard
  const copyToClipboard = (key: string, value: string) => {
    if (!value || value === '—') return;
    
    navigator.clipboard.writeText(value).then(() => {
      setCopiedFields(prev => ({ ...prev, [key]: true }));
      
      // Reset copied status after 2 seconds
      setTimeout(() => {
        setCopiedFields(prev => ({ ...prev, [key]: false }));
      }, 2000);
    });
  };
  
  // Render field value with eye/copy buttons for sensitive data
  const renderFieldValue = (field: { label: string, value: string }) => {
    const isSecret = field.label.toLowerCase().includes('api key') || 
                    field.label.toLowerCase().includes('token') || 
                    field.label.toLowerCase().includes('password');
    
    // Fields that should have copy button
    const shouldHaveCopyButton = 
      field.label.toLowerCase() === 'tenant code' ||
      field.label.toLowerCase().includes('uuid') ||
      field.label.toLowerCase().includes('url') ||
      field.label.toLowerCase().includes('api key') ||
      field.label.toLowerCase().includes('email') ||
      field.label.toLowerCase().includes('provider') ||
      field.label.toLowerCase().includes('destination');
    
    const fieldKey = field.label.replace(/\s+/g, '_').toLowerCase();
    
    if (isSecret && field.value !== '—') {
      // Get the actual value from tenant object based on the field key
      let actualValue = '';
      if (fieldKey === 'soar_api_key') {
        actualValue = tenant.soar_api_key || '';
      } else if (fieldKey === 'vision_one_api_key') {
        actualValue = tenant.vision_one_api_key || '';
      } else if (fieldKey === 'vision_one_token') {
        actualValue = getTenantProperty('vision_one_token') || '';
      } else {
        // For other potential secret fields
        actualValue = getTenantProperty(fieldKey) || '';
      }
      
      const displayValue = visibleSecrets[fieldKey] ? actualValue : '••••••••';
      
      return (
        <div className="flex items-center">
          <span className="mr-2">{displayValue}</span>
          <div className="flex">
            <button
              type="button"
              onClick={() => toggleSecretVisibility(fieldKey)}
              className="p-1 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
              title={visibleSecrets[fieldKey] ? "Hide value" : "Show value"}
            >
              {visibleSecrets[fieldKey] ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button
              type="button"
              onClick={() => copyToClipboard(fieldKey, actualValue)}
              className="p-1 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
              title="Copy to clipboard"
            >
              {copiedFields[fieldKey] ? <CheckCheck size={14} className="text-green-400" /> : <Copy size={14} />}
            </button>
          </div>
        </div>
      );
    }
    
    // For non-secret fields that can be copied
    if (shouldHaveCopyButton && field.value && field.value !== '—') {
      return (
        <div className="flex items-center">
          <span className="mr-2">{field.value}</span>
          <button
            type="button"
            onClick={() => copyToClipboard(fieldKey, field.value)}
            className="p-1 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-200 transition-colors"
            title="Copy to clipboard"
          >
            {copiedFields[fieldKey] ? <CheckCheck size={14} className="text-green-400" /> : <Copy size={14} />}
          </button>
        </div>
      );
    }
    
    return field.value;
  };

  // Define sections for the tenant details with comprehensive field mappings
  const detailSections = [
    {
      title: 'Basic Information',
      icon: <Building size={16} className="mr-2 text-primary" />,
      fields: [
        { label: 'Tenant Name', value: tenant.name || '—' },
        { label: 'Tenant Code', value: tenant.tenant_code || '—' },
        { label: 'UUID', value: tenant.uuid || '—' },
        { label: 'Tenant Type', value: getTenantTypeLabel(tenant.tenant_type) || '—' },
        { label: 'Tenant Level', value: getTenantLevelLabel(tenant.level) || '—' },
        { label: 'Group', value: tenant.group || '—' },
        { label: 'Status', value: tenant.status || '—' },
        { label: 'Parent Tenant', value: parentTenantName || tenant.parent_tenant_id || '—' },
        { label: 'Country', value: (tenant as any).country || '—' },
        { label: 'Industry', value: (tenant as any).industry && (tenant as any).industry.length > 0 ? (tenant as any).industry.join(', ') : '—' },
        { label: 'Head Quarter', value: (tenant as any).head_quarter || '—' },
        // { label: 'Description', value: getTenantProperty('description') },
      ]
    },
    {
      title: 'Dates & Contract',
      icon: <Calendar size={16} className="mr-2 text-primary" />,
      fields: [
        { label: 'Start Date', value: tenant.start_date ? formatDate(tenant.start_date) : '—' },
        { label: 'End Date', value: tenant.end_date ? formatDate(tenant.end_date) : '—' },
        { label: 'Onboarding Date', value: tenant.onboarding_date ? formatDate(tenant.onboarding_date) : '—' },
        { label: 'Contract Renewal Status', value: tenant.contract_renewal_status || '—' },
        { label: 'Created At', value: tenant.created_at ? formatDate(tenant.created_at) : '—' },
        { label: 'Updated At', value: getTenantProperty('updated_at') ? formatDate(getTenantProperty('updated_at')) : '—' },
      ]
    },
    {
      title: 'Retention & Usage',
      icon: <Clock size={16} className="mr-2 text-primary" />,
      fields: [
        { label: 'Threshold per Day (GB)', value: tenant.threshold_per_day?.toString() || '—' },
        { label: 'Online Retention (days)', value: tenant.online_retention ? `${tenant.online_retention} days` : '—' },
        { label: 'Offline Retention (days)', value: tenant.offline_retention ? `${tenant.offline_retention} days` : '—' },
        // { label: 'Storage Path', value: getTenantProperty('storage_path') },
        // { label: 'Bucket Name', value: getTenantProperty('bucket_name') },
      ]
    },
    {
      title: 'SOAR Configuration',
      icon: <Server size={16} className="mr-2 text-primary" />,
      fields: [
        { label: 'API URL', value: tenant.soar_api_url || '—' },
        { label: 'Tenant ID', value: tenant.soar_tenant_id || '—' },
        { label: 'API Key', value: tenant.soar_api_key ? tenant.soar_api_key : '—' },
        // { label: 'SOAR Instance', value: getTenantProperty('soar_instance') },
      ]
    },
    {
      title: 'Vision One Configuration',
      icon: <Shield size={16} className="mr-2 text-primary" />,
      fields: [
        { label: 'Vision One URL', value: tenant.vision_one_url || '—' },
        { label: 'Vision One API Key', value: tenant.vision_one_api_key ? tenant.vision_one_api_key : '—' },
        { label: 'Scan Action', value: tenant.scan_action || '—' },
        { label: 'Vision One Token', value: getTenantProperty('vision_one_token') || '—' },
        // { label: 'Vision One Instance', value: getTenantProperty('vision_one_instance') },
      ]
    },
    {
      title: 'Email Configuration',
      icon: <Mail size={16} className="mr-2 text-primary" />,
      fields: [
        { label: 'From Email', value: (tenant as any).from_email || '—' },
        { label: 'Email To', value: tenant.email_to || '—' },
        { label: 'Email CC', value: tenant.email_cc || '—' },
        // { label: 'Email Configuration', value: getTenantProperty('email_configuration') ? JSON.stringify(getTenantProperty('email_configuration')) : '—' },
      ]
    },
    {
      title: 'Feature Flags',
      icon: <Tag size={16} className="mr-2 text-primary" />,
      fields: [
        { label: 'Attached IOC', value: displayBooleanValue(tenant.attached_ioc) },
        { label: 'Masking IOC', value: displayBooleanValue(tenant.masking_ioc) },
        { label: 'Sweeping on Vision One', value: displayBooleanValue(tenant.sweeping_on_vision_one) },
        { label: 'Sweeping on ESDL', value: displayBooleanValue(tenant.sweeping_on_esdl) },
        // { label: 'Has Source', value: displayBooleanValue(getTenantProperty('has_source', false)) },
        // { label: 'Show Welcome', value: displayBooleanValue(getTenantProperty('show_welcome', false)) },
      ]
    },
    {
      title: 'IDP Configuration',
      icon: <Lock size={16} className="mr-2 text-primary" />,
      fields: [
        { label: 'Provider Name', value: tenant.provider_name || '—' },
        { label: 'Destination', value: tenant.destination || '—' },
        // { label: 'Auth Provider', value: getTenantProperty('auth_provider') },
        // { label: 'Auth Configuration', value: getTenantProperty('auth_configuration') ? JSON.stringify(getTenantProperty('auth_configuration')) : '—' },
      ]
    },
    {
      title: 'Additional Information',
      icon: <FileText size={16} className="mr-2 text-primary" />,
      fields: [
        // { label: 'Attributes', value: getTenantProperty('attributes') ? JSON.stringify(getTenantProperty('attributes')) : '—' },
        // { label: 'Tags', value: getTenantProperty('tags') ? JSON.stringify(getTenantProperty('tags')) : '—' },
        // { label: 'Metadata', value: getTenantProperty('metadata') ? JSON.stringify(getTenantProperty('metadata')) : '—' },
        // { label: 'Settings', value: getTenantProperty('settings') ? JSON.stringify(getTenantProperty('settings')) : '—' },
        { label: 'Created By', value: formatUserDisplay(tenant.created_by, creatorName) },
        { label: 'Updated By', value: formatUserDisplay(getTenantProperty('updated_by'), updaterName) },
      ]
    },
  ];

  // Function to open the delete confirmation modal
  const openDeleteConfirmation = (productId: string, productName: string) => {
    setDeleteInfo({ productId, productName });
    setDeleteModalOpen(true);
  };

  // Handle downloading logger config file
  const handleDownloadConfig = async (productId: string, productName: string) => {
    if (!tenant || !tenant.id) {
      toast.error('Tenant information not available');
      return;
    }

    try {
      toast.loading('Generating config file...', { id: 'download-config' });
      
      // Generate config file for this specific product
      const configData = await generateConfigFile(tenant.id, undefined, productId);
      
      if (configData && configData.template_content) {
        // Create a blob with the config content
        const blob = new Blob([configData.template_content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = configData.filename || `${productName.toLowerCase().replace(/\s+/g, '_')}_rsyslog.conf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Config file downloaded successfully', { id: 'download-config' });
      } else {
        toast.error('Failed to generate config file', { id: 'download-config' });
      }
    } catch (error: any) {
      console.error('Error downloading config file:', error);
      toast.error(error.response?.data?.error || 'Failed to download config file', { id: 'download-config' });
    }
  };

  // Handle delete integration confirmation
  const confirmDeleteIntegration = async () => {
    if (!deleteInfo || !tenant) return;
    
    try {
      // Find the product to determine its type
      const product = products.find(p => (p.id || p._id) === deleteInfo.productId);
      if (!product) {
        throw new Error('Product not found');
      }
      
      const productType = (product as any).type || 'saas';
      
      if (productType === 'logger') {
        // Delete logger integration
        // This might require a different API endpoint depending on your backend
        await deleteProductIntegration({
          customer_id: tenant.id,
          product_id: deleteInfo.productId
        });
      } else {
        // Delete regular SaaS integration
        await deleteProductIntegration({
          customer_id: tenant.id,
          product_id: deleteInfo.productId
        });
      }
      
      // Update local state
      setProductIntegrations(prev => {
        const updated = { ...prev };
        updated[deleteInfo.productId] = null;
        return updated;
      });
      
      // Show success message
      toast.success(`${deleteInfo.productName} integration has been deleted`);
      
      // Close modal
      setDeleteModalOpen(false);
      setDeleteInfo(null);
    } catch (error) {
      console.error('Error deleting integration:', error);
      toast.error('Failed to delete integration');
    }
  };

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        title="Tenant Details"
        size="xl"
      >
        {!canViewSection('tenant') ? (
          <div className="p-6 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h3 className="text-lg font-medium mb-2">Access Restricted</h3>
            <p className="text-sm text-gray-400 mb-4">
              You do not have permission to view this tenant's details.
            </p>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <Tabs
                tabs={[
                  { id: 'details', label: 'Details', icon: <Building size={16} /> },
                  { id: 'documents', label: 'Documents', icon: <FileText size={16} /> },
                  { id: 'products', label: 'Products', icon: <Package size={16} /> },
                  { id: 'users', label: 'Users', icon: <Users size={16} /> }
                ]}
                activeTab={activeTab}
                onChange={(id) => setActiveTab(id as 'details' | 'documents' | 'products' | 'users')}
              />
            </div>

            {activeTab === 'details' && (
              <div className="space-y-6 custom-scrollbar modal-content h-[calc(70vh-120px)] overflow-y-auto">
                {/* Header section with gradient background */}
                <div className="flex justify-between items-start p-4">
                  <div className="flex items-center">
                    <div className="h-16 w-16 rounded-lg overflow-hidden border border-gray-700 bg-white flex items-center justify-center shadow-lg">
                      {tenant.logo_url ? (
                        <img 
                          src={tenant.logo_url} 
                          alt={tenant.name}
                          className="w-full h-full object-contain"
                          onLoad={() => {}}
                          onError={(e) => {
                            console.error(`Error loading logo for tenant ${tenant.name} in detail view:`, e);
                            // Fallback to default icon if image fails to load
                            e.currentTarget.style.display = 'none';
                            (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`h-full w-full flex items-center justify-center ${tenant.logo_url ? 'hidden' : ''}`}
                      >
                        <Building size={24} className="text-gray-500" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <h2 className="text-xl font-semibold text-white flex items-center flex-wrap gap-2">
                        <span>{tenant.name}</span>
                        {tenant.tenant_code && (
                          <span className="inline-flex items-center gap-1.5">
                            <code className="text-xs text-gray-400 bg-slate-800/80 px-2 py-0.5 rounded">
                              {tenant.tenant_code}
                            </code>
                            <button
                              type="button"
                              onClick={() => copyToClipboard('header_tenant_code', tenant.tenant_code)}
                              className="p-1 hover:bg-gray-700/50 rounded-md text-gray-400 hover:text-gray-100 transition-colors"
                              title="Copy tenant code"
                            >
                              {copiedFields.header_tenant_code ? (
                                <CheckCheck size={14} className="text-green-400" />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </span>
                        )}
                      </h2>
                      <div className="flex items-center text-sm text-gray-400 mt-0.5">
                        <span>{getTenantTypeLabel(tenant.tenant_type)}</span>
                        <span className="mx-2 text-gray-600">•</span>
                        <span>{getTenantLevelLabel(tenant.level)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {canEditTenants() && !tenant?.is_deleted && (
                      <button
                        onClick={() => setIsEditModalOpen(true)}
                        className="flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded px-3 py-1.5 text-white text-sm"
                        title="Edit Tenant"
                      >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="round" className="mr-1.5">
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                      </svg>
                      Edit
                    </button>
                    )}
                  </div>
                </div>

                {/* Detail sections based on the review page */}
                <div className="space-y-6">
                  {detailSections.map((section, index) => (
                    <React.Fragment key={index}>
                      {/* Asset Repo - render before Additional Information */}
                      {section.title === 'Additional Information' && (
                        <div className="border border-slate-700/50 rounded-lg overflow-hidden bg-background-card">
                          <div className="bg-slate-800/30 px-4 py-2 font-medium text-gray-300 flex items-center">
                            <Shield size={16} className="mr-2 text-primary" />
                            Asset Repo
                          </div>
                          <div className="p-4">
                            {assetManagementIocs && Array.isArray(assetManagementIocs) && assetManagementIocs.length > 0 ? (
                              <div className="space-y-2">
                                {assetManagementIocs.map((ioc: any, iocIndex: number) => (
                                  <div key={iocIndex} className="p-2 bg-slate-800/50 rounded text-sm">
                                    <span className="text-gray-400">Type: </span>
                                    <span className="text-white">{ioc.type || 'N/A'}</span>
                                    {' | '}
                                    <span className="text-gray-400">Value: </span>
                                    <span className="text-white break-all">{ioc.value || 'N/A'}</span>
                                    {' | '}
                                    <span className="text-gray-400">Action: </span>
                                    <span className={ioc.action === 'blocked' ? 'text-red-400' : 'text-green-400'}>
                                      {ioc.action || 'N/A'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">No Asset configured</span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="border border-slate-700/50 rounded-lg overflow-hidden bg-background-card">
                        <div className="bg-slate-800/30 px-4 py-2 font-medium text-gray-300 flex items-center">
                          {section.icon}
                          {section.title}
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {section.fields.map((field, j) => (
                              <div key={j} className="flex flex-col px-4 py-3 rounded-lg bg-slate-800/20 hover:bg-slate-800/30 transition-colors">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">{field.label}</span>
                                <div className="text-sm text-gray-200">{renderFieldValue(field)}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="custom-scrollbar modal-content h-[calc(70vh-120px)] overflow-y-auto">
                <div className="border border-slate-700/50 rounded-lg overflow-hidden bg-background-card">
                  <div className="bg-slate-800/70 px-4 py-2.5 font-medium text-gray-300 flex items-center">
                    <FileText size={16} className="mr-2 text-primary" />
                    SOW &amp; Documents
                  </div>
                  <div className="p-4">
                    {(tenant as any)?.sow_documents && (tenant as any).sow_documents.length > 0 ? (
                      <div className="space-y-3">
                        {(tenant as any).sow_documents.map((doc: SOWDocument) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3"
                          >
                            <div className="flex items-center min-w-0 flex-1">
                              <div className="w-10 h-10 rounded-md border border-slate-700 bg-slate-800 flex items-center justify-center shrink-0">
                                <FileText size={18} className="text-primary" />
                              </div>
                              <span className="ml-3 text-sm text-gray-300 truncate" title={doc.name}>
                                {doc.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 ml-3 shrink-0">
                              {doc.url && (
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:text-primary/80 flex items-center"
                                >
                                  <Download size={14} className="mr-1" />
                                  Download
                                </a>
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedDocument(doc);
                                  setIsDocumentViewerOpen(true);
                                }}
                              >
                                <Eye size={14} className="mr-1" />
                                View
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <FileText size={40} className="mx-auto mb-3 opacity-40" />
                        <p className="text-sm">No documents uploaded for this tenant.</p>
                        {canEditTenants() && !tenant?.is_deleted && (
                          <p className="text-xs text-gray-500 mt-2">
                            Use Edit Tenant → Documents tab to upload files.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="custom-scrollbar modal-content h-[calc(70vh-120px)] overflow-y-auto">
                <div className="mb-4 flex justify-between items-center">
                  
                  {/* Product filter */}
                  <div className="flex items-center bg-slate-800/50 rounded-md border border-slate-700/50 p-1.5">
                    {/* <button
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${productFilter === 'all' ? 'bg-primary/20 text-primary' : 'hover:bg-slate-700/50 text-gray-300'}`}
                      onClick={() => setProductFilter('all')}
                    >
                      All
                    </button> */}
                    <button
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${productFilter === 'integrated' ? 'bg-primary/20 text-primary' : 'hover:bg-slate-700/50 text-gray-300'}`}
                      onClick={() => setProductFilter('integrated')}
                    >
                      Integrated
                    </button>
                    {/* <button
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${productFilter === 'not-integrated' ? 'bg-primary/20 text-primary' : 'hover:bg-slate-700/50 text-gray-300'}`}
                      onClick={() => setProductFilter('not-integrated')}
                    >
                      Not Integrated
                    </button> */}
                  </div>
                  
                  {/* Product type filter */}
                  <div className="flex items-center bg-slate-800/50 rounded-md border border-slate-700/50 p-1.5">
                    <button
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${productTypeFilter === 'all' ? 'bg-primary/20 text-primary' : 'hover:bg-slate-700/50 text-gray-300'}`}
                      onClick={() => setProductTypeFilter('all')}
                    >
                      All Types
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${productTypeFilter === 'saas' ? 'bg-primary/20 text-primary' : 'hover:bg-slate-700/50 text-gray-300'}`}
                      onClick={() => setProductTypeFilter('saas')}
                    >
                      SaaS
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${productTypeFilter === 'logger' ? 'bg-primary/20 text-primary' : 'hover:bg-slate-700/50 text-gray-300'}`}
                      onClick={() => setProductTypeFilter('logger')}
                    >
                      Logger
                    </button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="py-12 flex justify-center bg-background-card rounded-lg border border-slate-700/50 min-h-[300px]">
                    <div className="relative">
                      <div className="w-12 h-12 border-4 border-slate-700/70 border-t-primary rounded-full animate-spin"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Package className="h-5 w-5 text-primary/70" />
                      </div>
                    </div>
                  </div>
                ) : products.length === 0 ? (
                  <div className="py-8 text-center bg-background-card rounded-lg border border-slate-700/50 p-6 min-h-[300px] flex flex-col justify-center">
                    <Package className="h-12 w-12 mx-auto mb-4 text-slate-500 opacity-50" />
                    <p className="text-sm text-gray-400 mb-4">No products available for integration</p>
                    <Link to="/products" className="bg-slate-800/70 hover:bg-slate-700 text-primary px-4 py-2 rounded border border-slate-700/50 flex items-center justify-center mx-auto w-max">
                      <Plus size={16} className="mr-2" />
                      Add Products
                    </Link>
                  </div>
                ) : (
                  <>
                    {/* Product counts */}
                    <div className="mb-3 text-sm">
                      {(() => {
                        const filteredCount = products.filter(product => {
                          const productId = product.id || product._id as string;
                          const isIntegrated = !!productIntegrations[productId];
                          const productType = (product as any).type || 'saas';
                          
                          // Filter by integration status
                          const matchesIntegrationFilter = 
                            productFilter === 'all' ? true :
                            productFilter === 'integrated' ? isIntegrated :
                            !isIntegrated;
                          
                          // Filter by product type
                          const matchesTypeFilter = 
                            productTypeFilter === 'all' ? true :
                            productTypeFilter === productType;
                          
                          return matchesIntegrationFilter && matchesTypeFilter;
                        }).length;
                        
                        return (
                          <div className="flex items-center text-gray-400">
                            <span className="bg-slate-800/70 py-1.5 px-3 rounded-md border border-slate-700/50 text-xs">
                              {filteredCount} product{filteredCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-4">
                      {products
                        .filter(product => {
                          const productId = product.id || product._id as string;
                          const isIntegrated = !!productIntegrations[productId];
                          const productType = (product as any).type || 'saas';
                          
                          // Filter by integration status
                          const matchesIntegrationFilter = 
                            productFilter === 'all' ? true :
                            productFilter === 'integrated' ? isIntegrated :
                            !isIntegrated;
                          
                          // Filter by product type
                          const matchesTypeFilter = 
                            productTypeFilter === 'all' ? true :
                            productTypeFilter === productType;
                          
                          return matchesIntegrationFilter && matchesTypeFilter;
                        })
                        .map(product => {
                          const productId = product.id || product._id as string;
                          const integration = productIntegrations[productId];
                          const isIntegrated = !!integration;
                          const productType = (product as any).type || 'saas';
                          
                          return (
                            <div 
                              key={productId} 
                              className={`relative bg-background-card rounded-lg overflow-hidden border transition-colors flex flex-col ${isIntegrated ? 'border-primary/30 hover:border-primary/50' : 'border-slate-700/50 hover:border-slate-600'} ${isIntegrated ? 'cursor-pointer' : ''}`}
                              onClick={(e) => {
                                // Only open integration details if click wasn't on a button or input
                                const target = e.target as HTMLElement;
                                const isClickableElement = 
                                  target.tagName === 'BUTTON' || 
                                  target.closest('button') || 
                                  target.tagName === 'INPUT' || 
                                  target.closest('input');
                                
                                if (isIntegrated && !isClickableElement) {
                                  setIntegrationDetailsModal({
                                    isOpen: true,
                                    integration,
                                    productName: product.name
                                  });
                                }
                              }}
                            >
                              {/* Card Header */}
                              <div className="p-3 border-b border-slate-700/30">
                                <div className="flex justify-between items-start">
                                  {/* Product info */}
                                  <div className="flex items-center">
                                    <div className="w-8 h-8 bg-slate-800/70 rounded-md flex items-center justify-center mr-2 border border-slate-700/30">
                                      {product.logo_url ? (
                                        <img 
                                          src={product.logo_url} 
                                          alt={product.name} 
                                          className="max-w-full max-h-full object-contain" 
                                        />
                                      ) : (
                                        <Package className="h-5 w-5 text-primary" />
                                      )}
                                    </div>
                                    <div>
                                      <h3 className="font-medium text-white text-sm">{product.name}</h3>
                                      <div className="flex items-center mt-0.5 text-xs">
                                        {/* Product type badge */}
                                        <span className={`rounded px-1.5 py-0.5 text-xs ${
                                          productType === 'logger' 
                                            ? 'bg-green-700/20 text-green-300' 
                                            : 'bg-blue-700/20 text-blue-300'
                                        }`}>
                                          {productType === 'logger' ? 'Logger' : 'SaaS'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Add toggle switch and status label to top right */}
                                  {isIntegrated && (
                                    <div 
                                      id={`integration-status-${productId}`} 
                                      className="flex flex-col items-end"
                                      onClick={(e) => e.stopPropagation()} // Prevent card click when clicking toggle
                                    >
                                      <span className={`text-xs font-medium mb-1 ${integration?.active ? 'text-green-400' : 'text-red-400'}`}>{integration?.active ? 'Active' : 'Inactive'}</span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent card click when clicking toggle
                                          if (canManageIntegrations()) {
                                            toggleIntegrationStatus(productId, !!integration?.active);
                                          }
                                        }}
                                        className={`relative inline-flex items-center focus:outline-none ${canManageIntegrations() ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                                        aria-checked={!!integration?.active}
                                        role="switch"
                                        disabled={!canManageIntegrations()}
                                      >
                                        <div className={`w-10 h-5 rounded-full transition-colors duration-200 ease-in-out ${integration?.active ? 'bg-green-500' : 'bg-red-500'}`}>
                                          <div className={`absolute top-[2px] left-[2px] w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out ${integration?.active ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                        </div>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Card Body */}
                              <div className="p-3">
                                  {isIntegrated ? (
                                    <div className="flex items-center gap-3">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent card click
                                          setSelectedProductForIntegration({
                                            product,
                                            isIntegrated: true
                                          });
                                          setIsIntegrationModalOpen(true);
                                        }}
                                        className="flex-1 min-w-0 text-xs font-medium bg-primary/15 hover:bg-primary/25 text-primary px-3 py-2 rounded-lg border border-primary/30 flex items-center justify-center gap-2 transition-colors"
                                      >
                                        <Settings size={14} />
                                        <span className="truncate">Update Configuration</span>
                                      </button>
                                      {(productType === 'logger' || canManageIntegrations()) && (
                                      <div className="flex items-center gap-1.5 shrink-0 border-l border-slate-600/50 pl-3">
                                        {productType === 'logger' && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownloadConfig(productId, product.name);
                                            }}
                                            className="p-2 rounded-lg bg-slate-800/50 hover:bg-green-500/20 text-green-400 hover:text-green-300 transition-colors"
                                            title="Download Config File"
                                          >
                                            <Download size={16} />
                                          </button>
                                        )}
                                        {canManageIntegrations() && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openDeleteConfirmation(productId, product.name);
                                            }}
                                            className="p-2 rounded-lg bg-slate-800/50 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                                            title="Remove integration"
                                          >
                                            <Trash2 size={16} />
                                          </button>
                                        )}
                                      </div>
                                      )}
                                    </div>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedProductForIntegration({
                                          product,
                                          isIntegrated: false
                                        });
                                        setIsIntegrationModalOpen(true);
                                      }}
                                      className="w-full text-xs bg-primary hover:bg-primary-dark text-white px-2 py-1.5 rounded flex items-center justify-center"
                                    >
                                      <Plus size={12} className="mr-1.5" />
                                      Configure
                                    </button>
                                  )}
                              </div>
                            </div>  
                          );
                        })}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'users' && (
              <div className="h-[calc(70vh-120px)] overflow-y-auto">
                <div className="mb-4 flex justify-between items-center">
                  <p className="text-sm text-text-muted-dark">
                    Users associated with this tenant.
                  </p>
                  <Link 
                    to={`/users/new?tenant_id=${tenant.id}`}
                    className="btn-primary btn-sm flex items-center"
                  >
                    <Plus size={16} className="mr-1.5" />
                    Add User
                  </Link>
                </div>
                
                {isLoadingUsers ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span className="ml-3 text-sm text-gray-400">Loading users...</span>
                  </div>
                ) : tenantUsers.length === 0 ? (
                  <div className="text-center py-10 bg-background-card rounded-md border border-card-border">
                    <p className="text-sm text-gray-400 mb-4">No users found for this tenant.</p>
                    <Link 
                      to={`/users/new?tenant_id=${tenant.id}`}
                      className="btn-secondary btn-sm flex items-center justify-center mx-auto w-max"
                    >
                      <Plus size={16} className="mr-1.5" />
                      Add First User
                    </Link>
                  </div>
                ) : (
                  <div className="bg-background-card rounded-md border border-card-border overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-800/30 text-sm text-left">
                          <th className="py-3 px-4 text-gray-400 font-medium">Name</th>
                          <th className="py-3 px-4 text-gray-400 font-medium">Email</th>
                          <th className="py-3 px-4 text-gray-400 font-medium">Role</th>
                          <th className="py-3 px-4 text-gray-400 font-medium">Status</th>
                          <th className="py-3 px-4 text-gray-400 font-medium text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/30">
                        {tenantUsers.map(user => (
                          <tr key={user.id} className="hover:bg-slate-800/20">
                            <td className="py-2.5 px-4 text-sm">
                              <div className="flex items-center">
                                <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center mr-3">
                                  <span className="font-medium text-xs">
                                    {user.first_name && user.last_name 
                                      ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}` 
                                      : user.email.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span>{user.first_name && user.last_name 
                                  ? `${user.first_name} ${user.last_name}` 
                                  : user.email}
                                </span>
                              </div>
                            </td>
                            <td className="py-2.5 px-4 text-sm text-gray-300">{user.email}</td>
                            <td className="py-2.5 px-4 text-sm">
                              <span className="px-2 py-1 rounded-full text-xs bg-gray-800 text-gray-300">
                                {user.role || 'User'}
                              </span>
                            </td>
                            <td className="py-2.5 px-4 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                user.status === 'active' 
                                  ? 'bg-green-900/30 text-green-400' 
                                  : 'bg-red-900/30 text-red-400'
                              }`}>
                                {user.status || 'inactive'}
                              </span>
                            </td>
                            <td className="py-2.5 px-4 text-sm text-right">
                              <Link 
                                to={`/users/${user.id}`}
                                className="text-primary hover:text-primary-hover"
                              >
                                <Edit size={16} />
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t border-card-border mt-6">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </Modal>
      
      {/* Edit Modal */}
      {isEditModalOpen && tenant && (
        <TenantFormModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSaved={() => {
            setIsEditModalOpen(false);
            onClose();
          }}
          tenantId={tenant.id}
        />
      )}
      
      {/* Product Integration Modal */}
      {selectedProductForIntegration && (
        <IntegrateProductModal
          isOpen={isIntegrationModalOpen}
          onClose={() => {
            setIsIntegrationModalOpen(false);
            setSelectedProductForIntegration(null);
            // Refresh product integrations after closing
            if (tenant) {
              fetchProducts();
            }
          }}
          product={selectedProductForIntegration.product}
          tenant={tenant!}
          isIntegrated={selectedProductForIntegration.isIntegrated}
          onIntegrationComplete={() => {
            // Refresh product integrations after integration is complete
            if (tenant) {
              fetchProducts();
            }
          }}
        />
      )}
      
      {/* Integration Details Modal */}
      {integrationDetailsModal.isOpen && (
        <IntegrationDetailsModal
          isOpen={integrationDetailsModal.isOpen}
          onClose={() => setIntegrationDetailsModal({...integrationDetailsModal, isOpen: false})}
          integration={integrationDetailsModal.integration}
          productName={integrationDetailsModal.productName}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDeleteIntegration}
        title="Delete Integration"
        message={`Are you sure you want to delete the ${deleteInfo?.productName} integration? This action cannot be undone.`}
        confirmButtonText="Delete Integration"
        cancelButtonText="Cancel"
        isDanger={true}
      />
      
      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && selectedDocument && (
        <>
          {/* DocumentViewer component */}
          <DocumentViewer
            isOpen={isDocumentViewerOpen}
            onClose={() => {
              // DocumentViewer closed
              setIsDocumentViewerOpen(false);
              setSelectedDocument(null);
            }}
            documentUrl={selectedDocument.url}
            documentName={selectedDocument.name}
          />
        </>
      )}
    </>
  );
};

export default TenantDetailModal; 